import { useEffect } from 'react';

// Chess piece Unicode symbols for favicon animation
const CHESS_PIECES = [
  '♙', // White Pawn
  '♘', // White Knight
  '♗', // White Bishop
  '♖', // White Rook
  '♕', // White Queen
  '♔', // White King
  '♟', // Black Pawn
  '♞', // Black Knight
  '♝', // Black Bishop
  '♜', // Black Rook
  '♛', // Black Queen
  '♚'  // Black King
];

// Function to create a canvas-based favicon with a chess piece
const createFavicon = (piece) => {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  
  // Clear the canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  // Set background
  ctx.fillStyle = '#f0d9b5'; // Light chess square color
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Draw the chess piece
  ctx.fillStyle = piece.charCodeAt(0) > 9817 ? '#000000' : '#8B4513'; // Black or dark brown
  ctx.font = '48px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(piece, canvas.width/2, canvas.height/2);
  
  return canvas.toDataURL('image/png');
};

// Function to set the favicon
const setFavicon = (faviconUrl) => {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
};

const AnimatedFavicon = () => {
  useEffect(() => {
    // Create a default favicon with a random piece at startup
    const defaultPiece = CHESS_PIECES[Math.floor(Math.random() * CHESS_PIECES.length)];
    const defaultFavicon = createFavicon(defaultPiece);
    
    // Save the original favicon if one exists
    const originalFavicon = document.querySelector("link[rel~='icon']")?.href;
    
    // Animation parameters
    const startSpinningSpeed = 80; // ms between changes at start
    const maxDuration = 3000; // total animation duration in ms
    const slowdownFactor = 1.25; // how quickly to slow down
    
    // Create a shuffled array of all chess pieces
    const shuffledPieces = [...CHESS_PIECES].sort(() => Math.random() - 0.5);
    // Add the final piece that we'll end on
    const finalPiece = CHESS_PIECES[Math.floor(Math.random() * CHESS_PIECES.length)];
    shuffledPieces.push(finalPiece);
    
    // Animation variables
    let startTime = Date.now();
    let currentSpeed = startSpinningSpeed;
    let animationTimer = null;
    let currentIndex = 0;
    
    const animateFavicon = () => {
      const elapsedTime = Date.now() - startTime;
      
      if (elapsedTime < maxDuration && currentIndex < shuffledPieces.length) {
        // Get the next piece from our shuffled array
        const nextPiece = shuffledPieces[currentIndex];
        const faviconUrl = createFavicon(nextPiece);
        setFavicon(faviconUrl);
        
        // Move to next piece
        currentIndex++;
        
        // If we've shown all pieces, start over with the shuffled array
        if (currentIndex >= shuffledPieces.length - 1 && elapsedTime < maxDuration - 500) {
          // Reshuffle before starting over, but keep the final piece the same
          const newShuffled = [...CHESS_PIECES].sort(() => Math.random() - 0.5);
          shuffledPieces.splice(0, shuffledPieces.length - 1, ...newShuffled);
          currentIndex = 0;
        }
        
        // Increase the delay between changes (slowing down)
        currentSpeed = startSpinningSpeed * Math.pow(slowdownFactor, elapsedTime / 500);
        
        // Schedule the next frame
        animationTimer = setTimeout(animateFavicon, currentSpeed);
      } else {
        // Animation ended, ensure we use the final piece
        const finalFavicon = createFavicon(finalPiece);
        setFavicon(finalFavicon);
      }
    };
    
    // Start the animation
    animationTimer = setTimeout(animateFavicon, currentSpeed);
    
    // Cleanup function
    return () => {
      clearTimeout(animationTimer);
      if (originalFavicon) {
        setFavicon(originalFavicon);
      }
    };
  }, []);
  
  // This component doesn't render anything
  return null;
};

export default AnimatedFavicon;